/**
 * Repairs Lexical rich-text editor state so it can't crash the editor or the
 * frontend renderer.
 *
 * The main offender is Lexical minified error #117 ("Invalid indent value"):
 * Lexical requires every node's `indent` to be a non-negative integer. Content
 * pasted from Word / Google Docs / the web (or produced by older importers) can
 * carry a negative, fractional, string, or NaN indent, which makes the editor
 * throw "Something went wrong" on load and white-screens the published page when
 * <RichText> renders it.
 *
 * `normalizeLexical` walks the tree and coerces any present `indent` to
 * `Math.max(0, Math.floor(Number(indent) || 0))`. It only touches nodes that
 * already have an `indent` property (so it never adds the field where Lexical
 * doesn't expect it), and returns a repaired deep copy — the input is not
 * mutated. Anything that isn't a Lexical state ({ root: {...} }) is returned
 * unchanged.
 */

function sanitizeIndent(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/**
 * Flatten a paragraph nested inside a paragraph. Older importers (WP → Lexical)
 * can emit `paragraph > paragraph > text`, which <RichText> renders as a literal
 * `<p><p>…</p></p>`. A `<p>` cannot contain another `<p>`: the browser auto-closes
 * the outer one and restructures the DOM, so React's client tree no longer matches
 * the server HTML → hydration error #418. We lift each child paragraph's own
 * children up into the parent, recursively (handles any depth). Valid Lexical never
 * nests paragraphs, so this only ever touches malformed/migrated content.
 */
function flattenNestedParagraphs(children: unknown[]): unknown[] {
  const out: unknown[] = [];
  for (const child of children) {
    const c = child as Record<string, unknown> | null;
    if (c && typeof c === 'object' && c.type === 'paragraph' && Array.isArray(c.children)) {
      out.push(...flattenNestedParagraphs(c.children as unknown[]));
    } else {
      out.push(child);
    }
  }
  return out;
}

/** Recursively repair a single node (and its children) in place.
 *
 * Lexical's `ElementNode.setIndent` / `ListItemNode.setIndent` throw #117
 * ("Invalid indent value") when `indent` is not a number — which includes a
 * MISSING indent (`undefined`) on a list item, not just a bad one. So every
 * element node (any node with a `children` array) is guaranteed a numeric,
 * non-negative, integer `indent`, whether or not it had the key before.
 *
 * Paragraph nodes additionally get any nested-paragraph children flattened, so
 * <RichText> can never emit invalid nested <p> (the #418 hydration cause). */
function repairNode(node: unknown): void {
  if (!node || typeof node !== 'object') return;
  const n = node as Record<string, unknown>;

  let children = n.children;
  const isElement = Array.isArray(children);

  if (isElement || 'indent' in n) n.indent = sanitizeIndent(n.indent);

  if (isElement) {
    if (n.type === 'paragraph') {
      children = flattenNestedParagraphs(children as unknown[]);
      n.children = children;
    }
    for (const child of children as unknown[]) repairNode(child);
  }
}

/** True when the value looks like a Lexical editor state. */
export function isLexicalState(value: unknown): value is { root: Record<string, unknown> } {
  return (
    !!value &&
    typeof value === 'object' &&
    'root' in (value as Record<string, unknown>) &&
    !!(value as Record<string, unknown>).root &&
    typeof (value as Record<string, unknown>).root === 'object'
  );
}

/**
 * Return a repaired deep copy of a Lexical state. Non-Lexical values pass
 * through unchanged.
 */
export function normalizeLexical<T>(state: T): T {
  if (!isLexicalState(state)) return state;
  const copy = structuredClone(state) as unknown as { root: Record<string, unknown> };
  repairNode(copy.root);
  return copy as unknown as T;
}

/**
 * Deep-walk an arbitrary value (e.g. a collection's `data`) and normalize every
 * Lexical state found anywhere inside it, in place. Used by the save hook so any
 * richText field — top-level, nested in a block, or inside an array — is
 * repaired regardless of where it sits. Returns the same reference for
 * convenience.
 */
export function normalizeLexicalDeep<T>(value: T, seen = new WeakSet<object>()): T {
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value as object)) return value;
  seen.add(value as object);

  if (isLexicalState(value)) {
    repairNode((value as unknown as { root: unknown }).root);
    return value;
  }

  if (Array.isArray(value)) {
    for (const item of value) normalizeLexicalDeep(item, seen);
    return value;
  }

  for (const key of Object.keys(value as Record<string, unknown>)) {
    normalizeLexicalDeep((value as Record<string, unknown>)[key], seen);
  }
  return value;
}
